import type {
	MantineStyleSystemProps,
	SpacingValue,
	SystemProp,
} from '@mantine/core';
import { Center, createStyles } from '@mantine/core';
import { colors } from '@repo/theme/primitives';
import type { ColorNames } from '@repo/theme/utils';
import { forwardRef, type CSSProperties } from 'react';
import type { IconAnimation } from '../Buttons/Button';
import { IconSizeMap, IconStrokeWidthMap, type IconSizes } from './constants';
import { TablerIconMap } from './iconMap';
import type { IconNames } from './types';

// Icon names and types moved to types.ts
const ICON_NAMES_TO_EXCLUDE = ['circleCheckFilled'];

function safeGetColor(
	getColor: (c: ColorNames) => string,
	color?: ColorNames
): string | undefined {
	if (!getColor) {
		// Something weird is happening here. `theme.other.getColor` is sometimes
		// undefined, despite the typesystem, so we guard against this case.
		return 'black';
	}

	if (!color) {
		// If no color is passed we return undefined so that we can have the
		// component rely on CSS colors
		return undefined;
	}

	// Sometimes the type system appers to not catch instances when were are
	// passing an invalid color here.
	try {
		const colorValue = getColor(color);
		return colorValue;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn(error);
		return colors.gray[8];
	}
}

type IIconStyleProps = {
	fillColor?: ColorNames;
};

const useStyles = createStyles((theme, { fillColor }: IIconStyleProps) => ({
	icon: {
		'& path': fillColor
			? {
					fill: theme.other.getColor(fillColor),
				}
			: {},
		// The classes will be applied when the animation prop exists
		'&.spin': {
			animation: 'spin 2s linear infinite',
		},
		'&.spinCounterClockwise': {
			animation: 'spinCounterClockwise 2s linear infinite',
		},
		'&.rotate90': {
			transform: 'rotate(90deg)',
		},
		'&.rotate180': {
			transform: 'rotate(180deg)',
		},
		'&.rotateCounterClockwise90': {
			transform: 'rotate(-90deg)',
		},
		'&.rotateCounterClockwise180': {
			transform: 'rotate(-180deg)',
		},
	},
}));

export interface IconProps
	extends Omit<MantineStyleSystemProps, 'display' | 'opacity'>,
		IIconStyleProps {
	/** Unique name of the icon. */
	name: IconNames;
	/** The color of the icon. Defaults the `icon/primary/default` */
	color?: ColorNames;
	/** The height and width of the icon. Defaults the 20px */
	size?: IconSizes;
	iconPadding?: SystemProp<SpacingValue>;
	iconWidth?: SystemProp<SpacingValue>;
	iconHeight?: SystemProp<SpacingValue>;
	className?: string;
	style?: CSSProperties;
	animation?: IconAnimation;
}

export const Icon = forwardRef<HTMLDivElement, IconProps>(
	(
		{
			iconWidth,
			iconHeight,
			iconPadding = 2,
			className,
			name,
			color,
			fillColor,
			size,
			animation,
			...others
		},
		ref
	) => {
		const { classes, cx, theme } = useStyles({ fillColor });

		let iconName = name;
		if (
			theme.colorScheme === 'dark' &&
			name.endsWith('Filled') &&
			!ICON_NAMES_TO_EXCLUDE.includes(name)
		) {
			const nonFilledName = name.replace('Filled', '') as IconNames;
			if (TablerIconMap[nonFilledName]) {
				iconName = nonFilledName;
			}
		}

		const IconToRender = TablerIconMap[iconName];

		const iconColorFromProp = color
			? safeGetColor(theme.other.getColor, color) ||
				safeGetColor(theme.other.getColor, 'icon/primary/default')
			: undefined;

		// This component consists of an box around a component.
		const iconSize = IconSizeMap[size ?? 'md'];
		const strokeWidth = IconStrokeWidthMap[size ?? 'md'];

		return (
			<Center
				p={iconPadding}
				w={iconWidth ?? iconSize}
				h={iconHeight ?? iconSize}
				ref={ref}
			>
				<IconToRender
					size={iconSize - 4}
					color={iconColorFromProp}
					className={cx(
						classes.icon,
						className,
						'mantine-Icon-icon',
						animation
					)}
					strokeWidth={strokeWidth}
					{...others}
				/>
			</Center>
		);
	}
);

Icon.displayName = 'Icon';
